import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Cigarette, Briefcase, Brain } from 'lucide-react';
import CTA from '../../components/CTA';

const LisburnQuitSmoking: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Quit Smoking Hypnotherapy Lisburn | Professional Smoking Cessation</title>
        <meta
          name="description"
          content="Professional quit smoking hypnotherapy serving Lisburn from Belfast clinic (8 miles, 15-20 min). Intensive 2-hour session. Break office smoking habits. 60-80% success rates."
        />
        <meta name="keywords" content="quit smoking Lisburn, stop smoking Lisburn, hypnotherapy smoking cessation Lisburn, quit vaping Lisburn, professional smoking cessation" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/lisburn-quit-smoking/" />
        <meta property="og:title" content="Quit Smoking Hypnotherapy Lisburn | Professional Smoking Cessation" />
        <meta property="og:description" content="Professional quit smoking hypnotherapy serving Lisburn. Intensive 2-hour session. Break office smoking break habits." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Smoking Cessation Lisburn",
            "description": "CNHC registered hypnotherapy for smoking cessation, serving Lisburn from Belfast clinic. Intensive 2-hour session with lifetime support and money-back guarantee. Specializing in professional clients and office smoking habits.",
            "url": "https://www.sharonclare-hypnotherapy.com/lisburn-quit-smoking/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Smoking Cessation Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Lisburn",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Wellness Clinic (Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can I schedule quit smoking sessions around my professional schedule?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. The Belfast clinic offers flexible scheduling with evening appointments available Wednesday through Friday (until 7:00 PM), making it convenient for Lisburn professionals who work standard office hours. The clinic is just 8 miles from Lisburn city centre (15-20 minute drive) or 23 minutes by train to Botanic station. Many clients schedule sessions for late afternoon or early evening, allowing them to quit smoking without disrupting their work commitments."
                }
              },
              {
                "@type": "Question",
                "name": "How can hypnotherapy help me break office smoking break habits?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Office smoking breaks are a powerful habit - they provide social connection, stress relief, and a legitimate reason to step away from your desk. Hypnotherapy rewires these cue-routine-reward patterns in your subconscious. We work on finding alternative stress relief strategies, managing the social aspect of workplace smoking culture, and breaking the automatic association between work stress and cigarettes. Many professional clients find this targeted approach more effective than willpower alone."
                }
              },
              {
                "@type": "Question",
                "name": "Is the session discreet for professional clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, complete confidentiality is maintained. The Belfast clinic, at Belfast Wellness Clinic on the Ormeau Road, is a professional therapy practice in a discreet location. Many clients appreciate that hypnotherapy for smoking cessation is a single intensive session (or small package), meaning minimal time away from work and no long-term commitment. You don't need to explain frequent appointments to colleagues or supervisors."
                }
              },
              {
                "@type": "Question",
                "name": "What are the success rates for professional clients quitting smoking?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Research shows 60-80% success rates for motivated clients who genuinely want to quit. Professional clients often have additional motivation - career progression, professional image, health insurance costs, setting an example for children. These factors can strengthen commitment to becoming a non-smoker. Hypnotherapy isn't magic, but when combined with genuine motivation, it's highly effective for breaking the psychological addiction that keeps professionals smoking despite knowing the health risks."
                }
              },
              {
                "@type": "Question",
                "name": "How far is the Belfast clinic from Lisburn?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, in south Belfast - the M1 into Belfast from Lisburn, then south along the Ormeau Road, typically around 20 minutes off-peak. Lisburn is also on the rail line into Belfast, with Botanic and City Hospital the closest stations to the clinic. For Lisburn professionals, this is a convenient location that's accessible without a long commute. The proximity makes it practical to attend sessions without taking significant time away from professional commitments."
                }
              },
              {
                "@type": "Question",
                "name": "Can I quit smoking without colleagues noticing withdrawal symptoms?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "One of the benefits of hypnotherapy for smoking cessation is that it addresses the psychological addiction, which significantly reduces irritability and cravings that typically accompany quitting. Many clients report they don't experience the mood swings, irritability, or difficulty concentrating that they feared. This makes it much easier to quit smoking discreetly without obvious withdrawal symptoms affecting your professional performance or relationships with colleagues."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Quit Smoking Hypnotherapy for Lisburn
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Break office smoking habits with intensive 2-hour session
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">8 miles</span> from Lisburn • <span className="font-semibold">15-20 min drive</span>
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Train className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">23 min train</span> Lisburn → Botanic station
                </p>
              </div>
              <p className="text-gray-600 mt-2">Evening appointments available • Perfect for professionals</p>
            </div>
            <div className="bg-white rounded-xl p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-start space-x-4">
                <Briefcase className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <p className="text-lg font-semibold text-gray-900 mb-2">Professional Image Investment</p>
                  <p className="text-gray-700">
                    <span className="font-semibold">One intensive session</span> vs. <span className="font-semibold">thousands of pounds a year on cigarettes</span>
                  </p>
                  <p className="text-accent-700 font-medium mt-1">Career health + life insurance savings + professional image</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Quit Smoking for Lisburn Professionals
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're a professional living in Lisburn and ready to quit smoking or vaping, specialist support is closer than you think. I serve Lisburn clients from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, in south Belfast. The journey is the M1 into Belfast and then south along the Ormeau Road, typically around 20 minutes off-peak. Lisburn is also on the rail line into Belfast, with Botanic and City Hospital the closest stations to the clinic. For Lisburn's professional demographic, this proximity makes specialist smoking cessation accessible without a long commute.
              </p>
              <p>
                As Northern Ireland's third-largest city with a strong professional and commuter class, Lisburn has unique smoking patterns. Office smoking breaks become deeply ingrained habits - not just about nicotine, but about stress relief, social connection, and a legitimate reason to step away from your desk. You might smoke during your commute, on lunch breaks, or immediately after stressful meetings. These habits are so automatic you barely remember deciding to smoke.
              </p>
              <p>
                Here's what you probably already know: smoking damages your professional image. Walking into a meeting smelling of cigarettes. Stepping outside for smoking breaks while colleagues stay at their desks. The judgment (spoken or unspoken) from non-smoking clients or senior management. The growing awareness that in 2026, smoking isn't just unhealthy - it's increasingly socially unacceptable in professional environments. And yet, despite knowing all this, you still smoke.
              </p>
              <p>
                That's because quitting smoking isn't about knowledge or willpower. It's about rewiring the subconscious habits that control your behavior. The automatic reach for a cigarette during your morning commute. The stress relief ritual of a smoking break after a difficult call. The social connection of joining smokers outside the office. These patterns are stored in your basal ganglia - the autopilot part of your brain - and willpower alone can't override them.
              </p>
              <p>
                Solution-focused hypnotherapy works by accessing your subconscious and rewiring these automatic patterns. Instead of fighting cravings with willpower (which depletes by evening), you naturally become a non-smoker. The intensive 2-hour session includes lifetime support and a money-back guarantee. For professionals, the investment pays for itself within weeks - not just in cigarette costs, but in professional image, health insurance premiums, and career confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Office Smoking Breaks - Professional Context */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Breaking Office Smoking Break Habits
            </h2>
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Briefcase className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Why Office Smoking Breaks Are So Hard to Quit
                    </h3>
                    <p className="text-gray-700 mb-4">
                      For many Lisburn professionals who commute to Belfast or work in Lisburn's business parks, office smoking breaks serve multiple purposes beyond nicotine:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Social connection:</strong> The smokers' community at work provides informal networking and relationship building</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Stress relief:</strong> A cigarette after a stressful meeting or difficult phone call feels like instant relaxation</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Legitimate break:</strong> Smoking gives you a socially acceptable reason to step away from your desk without looking lazy</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Mental reset:</strong> The physical act of going outside, breathing differently, and changing environment helps you think</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Commuter stress:</strong> The morning cigarette during your drive or train journey to work becomes a commuting ritual</span>
                      </li>
                    </ul>
                    <p className="text-gray-700 mt-4">
                      These psychological associations are why patches and gum rarely work for professionals - they address nicotine but ignore the real reasons you smoke. Hypnotherapy targets the subconscious patterns that drive office smoking habits.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Brain className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Hypnotherapy for Professional Smoking Cessation
                    </h3>
                    <p className="text-gray-700 mb-4">
                      The intensive 2-hour session is specifically designed to address workplace smoking patterns:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Identify your specific triggers:</strong> Stressful meetings, commuter stress, lunch breaks, after-work drinks - we map your personal smoking cues</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Reframe stress relief:</strong> Teaching your brain alternative stress management that doesn't involve cigarettes</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Address social aspects:</strong> Strategies for maintaining workplace relationships without joining smoking breaks</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Strengthen professional identity:</strong> Reinforcing your image as a non-smoker who values health, performance, and professional standards</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Subconscious rewiring:</strong> Deep hypnotherapy to break the automatic cue-routine-reward loops that control office smoking</span>
                      </li>
                    </ul>
                    <p className="text-gray-700 mt-4">
                      Many professional clients report they don't experience the irritability and mood swings they feared, making it possible to quit discreetly without withdrawal symptoms affecting work performance or colleague relationships.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional ROI Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              The Professional ROI of Quitting Smoking
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                Beyond the Financial Savings
              </h3>
              <p className="text-gray-700 mb-6">
                For Lisburn professionals, the return on investment for quitting smoking extends beyond the obvious financial savings:
              </p>
              <div className="bg-white rounded-xl p-6 mb-6">
                <p className="text-sm text-gray-600 mb-2">Annual Smoking Cost (20/day)</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">Thousands of pounds a year</p>
                <p className="text-sm text-gray-600">Hundreds of pounds every month going up in smoke</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <p className="text-lg font-semibold text-gray-900 mb-3">Professional Benefits:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Career progression:</strong> Smoking increasingly affects professional image and advancement opportunities</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Client relationships:</strong> No more concern about smelling of smoke during client meetings or presentations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Productivity:</strong> Fewer breaks, better focus, improved cognitive performance without nicotine addiction cycles</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Health insurance:</strong> Lower premiums for non-smokers, particularly relevant for private health insurance</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Role model:</strong> Setting a positive example for children without the hypocrisy of smoking while telling them not to</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Life insurance:</strong> Significant savings on life insurance premiums as a non-smoker</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                When you calculate the total cost - direct cigarette expenses, health insurance premiums, life insurance costs, dry cleaning bills, and the intangible professional image impact - smoking costs far more than the cigarettes alone. For Lisburn professionals focused on career advancement and family security, the investment in hypnotherapy is one of the smartest decisions you can make.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Lisburn Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "As a manager commuting from Lisburn to Belfast, I smoked on every break and during the drive. It was affecting my professional image - I could see colleagues' judgment when I came back from smoking breaks. One session with Sharon and I haven't smoked in 5 months. The train journey from Lisburn made it so convenient."
                </p>
                <p className="font-semibold text-gray-900">R.M., Lisburn City Centre - Management professional</p>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Office smoking breaks were my stress relief mechanism - after every difficult meeting, I'd automatically reach for cigarettes. Sharon helped me understand the cue-routine-reward pattern and gave me alternative stress management strategies. My work performance has actually improved since quitting because I'm not on a constant nicotine rollercoaster."
                </p>
                <p className="font-semibold text-gray-900">K.T., Lisburn - Financial services professional</p>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd switched to vaping thinking it was healthier, but I was just as addicted. As a professional, constantly vaping in the car park wasn't a good look. The intensive session was perfect for my schedule - one evening appointment and I was done. Six weeks smoke-free and vape-free, and my life insurance premium dropped significantly."
                </p>
                <p className="font-semibold text-gray-900">D.P., Hillsborough - IT professional</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Lisburn Quit Smoking
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I schedule quit smoking sessions around my professional schedule?
                </h3>
                <p className="text-gray-700">
                  Absolutely. The Belfast clinic offers flexible scheduling with evening appointments available Wednesday through Friday (until 7:00 PM), making it convenient for Lisburn professionals who work standard office hours. The clinic is just 8 miles from Lisburn city centre (15-20 minute drive) or 23 minutes by train to Botanic station. Many clients schedule sessions for late afternoon or early evening, allowing them to quit smoking without disrupting their work commitments.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How can hypnotherapy help me break office smoking break habits?
                </h3>
                <p className="text-gray-700">
                  Office smoking breaks are a powerful habit - they provide social connection, stress relief, and a legitimate reason to step away from your desk. Hypnotherapy rewires these cue-routine-reward patterns in your subconscious. We work on finding alternative stress relief strategies, managing the social aspect of workplace smoking culture, and breaking the automatic association between work stress and cigarettes. Many professional clients find this targeted approach more effective than willpower alone.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is the session discreet for professional clients?
                </h3>
                <p className="text-gray-700">
                  Yes, complete confidentiality is maintained. The Belfast clinic, at Belfast Wellness Clinic on the Ormeau Road, is a professional therapy practice in a discreet location. Many clients appreciate that hypnotherapy for smoking cessation is a single intensive session (or small package), meaning minimal time away from work and no long-term commitment. You don't need to explain frequent appointments to colleagues or supervisors.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What are the success rates for professional clients quitting smoking?
                </h3>
                <p className="text-gray-700">
                  Research shows 60-80% success rates for motivated clients who genuinely want to quit. Professional clients often have additional motivation - career progression, professional image, health insurance costs, setting an example for children. These factors can strengthen commitment to becoming a non-smoker. Hypnotherapy isn't magic, but when combined with genuine motivation, it's highly effective for breaking the psychological addiction that keeps professionals smoking despite knowing the health risks.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is the Belfast clinic from Lisburn?
                </h3>
                <p className="text-gray-700">
                  The Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, in south Belfast - the M1 into Belfast from Lisburn, then south along the Ormeau Road, typically around 20 minutes off-peak. Lisburn is also on the rail line into Belfast, with Botanic and City Hospital the closest stations to the clinic. For Lisburn professionals, this is a convenient location that's accessible without a long commute. The proximity makes it practical to attend sessions without taking significant time away from professional commitments.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I quit smoking without colleagues noticing withdrawal symptoms?
                </h3>
                <p className="text-gray-700">
                  One of the benefits of hypnotherapy for smoking cessation is that it addresses the psychological addiction, which significantly reduces irritability and cravings that typically accompany quitting. Many clients report they don't experience the mood swings, irritability, or difficulty concentrating that they feared. This makes it much easier to quit smoking discreetly without obvious withdrawal symptoms affecting your professional performance or relationships with colleagues.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default LisburnQuitSmoking;
